// =============================================================================
// theme.js - light / dark / system (UI Excellence item 7).
//
// Light is the default per Leonard's decision; "system" follows the OS and
// updates live if the OS flips while the app is open. The choice is stored per
// browser, not per user row, so it applies before any network call and never
// causes a flash of the wrong theme.
// =============================================================================
// Dark mode is generally available (2026-09-27). The staff app has zero
// hardcoded colour literals left (npm run check:dark, enforced in CI), so every
// staff user can choose Light, Dark or Auto from the sidebar. Light stays the
// default: nobody is switched without choosing.
//
// The preview opt-in below is kept only so the flags can be flipped back
// (READY=false, PREVIEW=true) without code changes elsewhere.
export const DARK_MODE_READY = true;       // general availability
export const DARK_MODE_PREVIEW = false;    // opt-in preview (superseded by READY)
const KEY = "pos-theme";          // "light" | "dark" | "system"
const mq = typeof window !== "undefined" && window.matchMedia ? window.matchMedia("(prefers-color-scheme: dark)") : null;

export function getThemePreference() {
  if (DARK_MODE_READY) {
    try { return localStorage.getItem(KEY) || "light"; } catch { return "light"; }
  }
  // Preview: honour a stored preference only if the user explicitly opted in.
  // Anyone else - and anyone who opted in before the flag existed - gets light.
  if (DARK_MODE_PREVIEW && isPreviewOptedIn()) {
    try { return localStorage.getItem(KEY) || "light"; } catch { return "light"; }
  }
  return "light";
}

const OPT_KEY = "pos-theme-preview-opt-in";
export function isPreviewOptedIn() {
  try { return localStorage.getItem(OPT_KEY) === "1"; } catch { return false; }
}
export function setPreviewOptIn(on) {
  try {
    if (on) localStorage.setItem(OPT_KEY, "1");
    else { localStorage.removeItem(OPT_KEY); localStorage.setItem(KEY, "light"); }
  } catch { /* private mode */ }
  applyTheme(getThemePreference());
}

export function resolveTheme(pref) {
  if (pref === "system") return mq && mq.matches ? "dark" : "light";
  return pref === "dark" ? "dark" : "light";
}

export function applyTheme(pref) {
  if (typeof document === "undefined") return;
  const resolved = resolveTheme(pref);
  document.documentElement.setAttribute("data-theme", resolved);
  // The brand SURFACE variables are derived per theme (see brandPalette), so the
  // palette has to be re-applied after the attribute changes. Without this the
  // page-header band keeps its light tint while the text flips pale, which is
  // what made every page title invisible in the first preview.
  if (typeof window !== "undefined" && window.__posReapplyBrandPalette) {
    try { window.__posReapplyBrandPalette(); } catch { /* palette not ready yet */ }
  }
}

export function setThemePreference(pref) {
  try { localStorage.setItem(KEY, pref); } catch { /* private mode */ }
  applyTheme(pref);
}

// Keep "system" live while the app is open.
export function watchSystemTheme() {
  if (!mq) return () => {};
  const h = () => { if (getThemePreference() === "system") applyTheme("system"); };
  mq.addEventListener("change", h);
  return () => mq.removeEventListener("change", h);
}
